import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

interface MediaPlayerProps {
  src: string;
  title?: string;
  artist?: string;
  coverSrc?: string;
  autoPlay?: boolean;
  className?: string;
  style?: CSSProperties;
}

function fmt(s: number) {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

export function MediaPlayer({
  src,
  title = "Untitled",
  artist,
  coverSrc,
  autoPlay = false,
  className,
  style,
}: MediaPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = volume;
    const onTime = () => setCurrent(a.currentTime);
    const onLoad = () => setDuration(a.duration);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoad);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoad);
      a.removeEventListener("ended", onEnd);
    };
  }, [volume]);

  function togglePlay() {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play();
      setPlaying(true);
    }
  }

  function seek(e: React.ChangeEvent<HTMLInputElement>) {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Number(e.target.value);
    setCurrent(Number(e.target.value));
  }

  function changeVolume(e: React.ChangeEvent<HTMLInputElement>) {
    const v = Number(e.target.value);
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  }

  const pct = duration ? (current / duration) * 100 : 0;

  return (
    <div className={cn("media-player", className)} style={style}>
      {src && <audio ref={audioRef} src={src} autoPlay={autoPlay} preload="metadata" />}
      {coverSrc && (
        <div className="media-cover">
          <img src={coverSrc} alt={title} />
        </div>
      )}
      <div className="media-info">
        <div className="media-title">{title}</div>
        {artist && <div className="media-artist">{artist}</div>}
      </div>
      <div className="media-progress-row">
        <span className="media-time">{fmt(current)}</span>
        <input
          type="range"
          className="media-range"
          min={0}
          max={duration || 1}
          step={0.1}
          value={current}
          onChange={seek}
          style={{ "--pct": `${pct}%` } as CSSProperties}
        />
        <span className="media-time">{fmt(duration)}</span>
      </div>
      <div className="media-controls">
        <button className="media-btn" onClick={togglePlay} aria-label={playing ? "Pause" : "Play"}>
          {playing ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          )}
        </button>
        <div className="media-volume">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="media-vol-icon">
            <path d="M11 5L6 9H2v6h4l5 4V5z"/>
            {volume > 0 && <path d="M15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round"/>}
          </svg>
          <input
            type="range"
            className="media-range media-range--volume"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            onChange={changeVolume}
            style={{ "--pct": `${volume * 100}%` } as CSSProperties}
          />
        </div>
      </div>
    </div>
  );
}
